import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { ValidatorResult } from './validatorRule.constants';
import { CardHolderNameRegionHelper } from './cardHolderNameRegionHelper';

export class CardHolderNameValidationMessages {
    public static readonly ERROR_KEYS = {
        BLANK: 'error.card_holder_name_required_jp_ap',
        BLANK_GENERIC: 'error.card_holder_name_required',
        MIN_LENGTH: 'error.invalid_length_too_small',
        MAX_LENGTH: 'error.invalid_length_too_large',
        INVALID_FORMAT: 'error.card_holder_name_invalid_jp_ap',
        INVALID_FORMAT_GENERIC: 'error.card_holder_name_invalid',
        INVALID_SYMBOLS: 'error.card_holder_name_invalid_symbols',
        PAN_DETECTED: 'error.card_holder_name_no_credit_card',
        SPECIAL_CHARS: 'error.card_holder_name_invalid_jp_ap',
    } as const;

    public static readonly WARNING_KEYS = {
        MANY_SPECIAL_CHARS: 'hint.card_holder_name_many_special_chars',
        CONTAINS_NUMBERS_OR_SYMBOLS: 'hint.card_holder_name_contains_numbers_or_symbols',
    } as const;

    public static readonly RULE_NAMES = {
        CANNOT_BE_BLANK: 'CannotBeBlank',
        INVALID_LENGTH_TOO_SMALL: 'InvalidLengthTooSmall',
        INVALID_LENGTH_TOO_LARGE: 'InvalidLengthTooLarge',
        INVALID_FORMAT: 'InvalidFormat',
        INVALID_SYMBOLS: 'InvalidSymbols',
        PAN_DETECTED: 'PANDetected',
        NO_NUMBERS: 'NoNumbers',
        NO_SPECIAL_CHARS: 'NoSpecialChars',
    } as const;

    public static createBlankError(value: string, region?: string): ValidatorResult {
        const key = CardHolderNameRegionHelper.isAsiaRegion(region)
            ? this.ERROR_KEYS.BLANK
            : this.ERROR_KEYS.BLANK_GENERIC;
        return {
            value,
            rule: this.RULE_NAMES.CANNOT_BE_BLANK,
            message: TranslationHelpers.getMessageDescriptor(key),
        };
    }

    public static createTooShortError(value: string, minLength: number = 2): ValidatorResult {
        return {
            value,
            rule: this.RULE_NAMES.INVALID_LENGTH_TOO_SMALL,
            message: {
                ...TranslationHelpers.getMessageDescriptor(this.ERROR_KEYS.MIN_LENGTH),
                values: { min: minLength },
            },
        };
    }

    public static createTooLongError(value: string, maxLength: number = 45): ValidatorResult {
        return {
            value,
            rule: this.RULE_NAMES.INVALID_LENGTH_TOO_LARGE,
            message: {
                ...TranslationHelpers.getMessageDescriptor(this.ERROR_KEYS.MAX_LENGTH),
                values: { max: maxLength },
            },
        };
    }

    public static createInvalidFormatError(value: string, region?: string): ValidatorResult {
        const key = CardHolderNameRegionHelper.isAsiaRegion(region)
            ? this.ERROR_KEYS.INVALID_FORMAT
            : this.ERROR_KEYS.INVALID_FORMAT_GENERIC;
        return {
            value,
            rule: this.RULE_NAMES.INVALID_FORMAT,
            message: TranslationHelpers.getMessageDescriptor(key),
        };
    }

    public static createPanError(value: string): ValidatorResult {
        return {
            value,
            rule: this.RULE_NAMES.PAN_DETECTED,
            message: TranslationHelpers.getMessageDescriptor(this.ERROR_KEYS.PAN_DETECTED),
        };
    }

    public static createNumbersOrSymbolsWarning(value: string): ValidatorResult {
        return {
            value,
            rule: this.RULE_NAMES.NO_NUMBERS,
            message: TranslationHelpers.getMessageDescriptor(
                this.WARNING_KEYS.CONTAINS_NUMBERS_OR_SYMBOLS,
            ),
        };
    }

    public static createNoSpecialCharsError(value: string): ValidatorResult {
        return {
            value,
            rule: this.RULE_NAMES.NO_SPECIAL_CHARS,
            message: TranslationHelpers.getMessageDescriptor(this.ERROR_KEYS.SPECIAL_CHARS),
        };
    }

    public static createInvalidSymbolsError(value: string): ValidatorResult {
        return {
            value,
            rule: this.RULE_NAMES.INVALID_SYMBOLS,
            message: TranslationHelpers.getMessageDescriptor(this.ERROR_KEYS.INVALID_SYMBOLS),
        };
    }
}
